import { Grid, Card, CardHeader, CardContent, Button, Typography, IconButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';

import { Contact } from '../../../../models/contact';
import { useFormContext, useWatch } from 'react-hook-form';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { proctorResultApiKeyStore } from '../../../../api/proctor-result-api-key-store';
import { columns } from './grid-columns';
import { ProctorResultDialog } from './proctor-result.dialog';
import { useFileDownload } from '../../../../components/file-download';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import { useSaveData } from '../../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../../hooks/use-debounce';

interface ProctorResultProps {
  name: string;
  label: string;
  page?: string;
  projectID?: number;
}

export const ProctorResultForm = ({ name, label, projectID }: ProctorResultProps) => {
  const { setValue, getValues } = useFormContext();
  const [proctorResultList, setProctorResultList] = useState<any[]>([]);
  const [editItem, setEditItem] = useState<Partial<any>>();
  const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<Contact[]>>(
    proctorResultApiKeyStore.getProjectProctorResults(projectID ?? 0),
    !!projectID
  );

  useEffect(() => {
    if (data) {
      setValue(name, data?.data);
      setProctorResultList(data?.data);
    }
  }, [data]);

  const handleCreateProctorResult = () => {
    setEditItem({});
  };

  const handleEditProctorResult = (proctorData: any) => {
    setEditItem(proctorData);
  };

  const handleSaveData = (newProctorResult: any) => {
    setValue('proctorResultsTempEntityID', newProctorResult?.proctorResultsTempEntityID);
    setValue(name, [...proctorResultList, newProctorResult]);
    setProctorResultList((prevSelectedPlans: any) => [...prevSelectedPlans, newProctorResult]);
    setEditItem(undefined);
  };

  const handleDownloadFile = async (data: any) => {
    const value = {
      proctorResultsID: Number(data?.proctorResultsID) || 0,
      fileName: data?.uploadedFileName,
      fileType: data?.fileType,
    };
    downloadFile({ additionalData: value });
  };

  const downloadFileElement = (dataItem: any) => {
    return (
      dataItem?.dataItem?.fileURL !== '' && (
        <IconButton aria-label="view" onClick={() => handleDownloadFile(dataItem?.dataItem)}>
          <Tooltip title={<Typography variant="body1">{'Download'}</Typography>}>
            <DownloadIcon className="btnEdit" />
          </Tooltip>
        </IconButton>
      )
    );
  };

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Project', 'Proctor'));

  // const { isLoading, data: Offices, refetch } = useLoadData<ApiResponse<Contact[]>>(getAPIKeyStore());

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredProctors, setFilteredProctors] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredProctorList = proctorResultList?.filter((contact: any) =>
      Object.values(contact).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredProctors(filteredProctorList || []);
  }, [debouncedSearchTerm, proctorResultList]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Proctor',
      pageName: 'Project',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Proctor',
      pageName: 'Project',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      //   if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      //   }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary">
            {label}
          </Typography>
        }
        action={
          // <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PLAN]}>
          <Button className="btnLink" variant="text" sx={{ p: 0, m: 0, mr: 1 }} onClick={handleCreateProctorResult}>
            Add Proctor Result
          </Button>
          // </GuardedComponent>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <SiTrackGrid
              fileTitle='project_proctor'
              //   dataItems={proctorResultList}
              dataItemKey="proctorResultsID"
              columns={columns}
              CustomComponent={downloadFileElement}
              
              onEdit={handleEditProctorResult}
              deleteMutationConfig={(item) => proctorResultApiKeyStore.deleteProctorResult(item.proctorResultsID)}
              actionPermissions={{
                download: UserPermissionName.DOWNLOAD_FILE,
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              isBusy={isLoading || isFetching || isFileDownloadLoading}
              // -----------------Grid Layout-------
              dataItems={filteredProctors || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            />
          </Grid>
        </Grid>
        {editItem && (
          <ProctorResultDialog
            proctorData={editItem}
            projectID={projectID}
            proctorResultsTempEntityID={getValues('proctorResultsTempEntityID') ?? ''}
            onSaveComplete={handleSaveData}
            onClosed={() => setEditItem(undefined)}
          />
        )}
      </CardContent>
    </Card>
  );
};
